import { useCallback, useState } from "react";

import { useAppRouteNavigation } from "@/app/app/_/routing";
import { StickyLayout } from "@/components/organisms/StickyLayout";
import { Thread } from "@/components/organisms/Thread/ThreadPanel";
import { ThreadListLayout } from "@/components/organisms/Thread/ThreadListLayout";
import { ThreadTabBar } from "@/components/organisms/Thread/ThreadTabBar";
import { useContextOverlay } from "@/hooks/useContextTree";
import { useLayoutColors } from "@/hooks/useLayoutColors";
import { State } from "@/state";

/**
 * The Thread panel — a global, view-independent panel docked to the far
 * right of the layout (full height, beside the entire content column +
 * Topbar 2 + Topbar 3). Pure thread; never shows a node. The thread list
 * can be toggled open: it drops down from under the tabs, over the thread,
 * since the panel is too narrow to give it a strip on the side. It starts
 * closed.
 *
 * Contrast with ``NodePanelLayout`` (contextual, per-view, sits below
 * Topbar 2) and ``ThreadPanelLayout`` (used inside the dedicated
 * ``/threads`` view, which can show a node *or* a thread).
 *
 * Focus nesting matches Dialog: callers ``pushFocusOrigin`` before
 * ``openThreadPanel`` (e.g. triage Chat / ``c``), then
 * ``useContextOverlay`` with ``bridge`` restores via ``popFocusOrigin``
 * when the panel closes.
 *
 * Unlike Dialog, this panel stays mounted while collapsed (width 0).
 * ``inert`` while closed mirrors Dialog unmount — TipTap cannot reclaim
 * focus after restore.
 *
 * Escape lets go of the chat rather than shutting it: it sits beside the
 * page, not over it, so focus goes back where it came from and the panel
 * stays open (``escape: "unfocus"``). TipTap still needs ``onEscape`` wired
 * to the same ``releaseFocus``, because the contenteditable swallows Escape
 * before context hotkeys.
 */
export function ThreadOnlyPanelLayout() {
  const colors = useLayoutColors();
  const isThreadPanelOpen = State((state) => state.isThreadPanelOpen);
  const [isListOpen, setIsListOpen] = useState(false);

  const onClose = useCallback(() => {
    State.getState().setIsThreadPanelOpen(false);
  }, []);

  // The same thread, given the page, and the panel closed so it is not shown
  // twice — in that order. The router applies a navigation as a transition,
  // while closing the panel is an immediate store update; closed in the click,
  // the store-to-URL sync ran against the route still on screen and wrote the
  // old view back, so the panel shut and the page never changed. It closes
  // once the thread view has landed instead, from the store rather than this
  // component, which the thread view does not render.
  const navigation = useAppRouteNavigation();
  const onExpand = useCallback(() => {
    const unsubscribe = State.subscribe((state) => {
      if (state.activeAppView !== "thread") return;
      unsubscribe();
      state.setIsThreadPanelOpen(false);
    });
    navigation.navigateToView("thread");
  }, [navigation]);

  const { Scope, releaseFocus } = useContextOverlay({
    isOpen: isThreadPanelOpen,
    onClose,
    escape: "unfocus",
    bridge: true,
    // Allow interacting with triage/list while the thread is open.
    trapFocus: false,
    // Triage Chat drives focus via ``openThreadPanel({ autofocus: true })``.
    autoFocus: false,
  });

  return (
    <Scope
      {...(!isThreadPanelOpen ? { inert: true } : {})}
      style={{
        display: "flex",
        flexDirection: "column",
        flex: 1,
        minHeight: 0,
        overflow: "hidden",
      }}
    >
      <StickyLayout
        stickyTopChildren={
          <ThreadTabBar
            isListOpen={isListOpen}
            onListToggle={() => setIsListOpen((open) => !open)}
            onClose={onClose}
          />
        }
        stickyTopClassName="[border-bottom-width:1px] [border-bottom-style:solid] border-border overflow-visible min-h-[unset]"
        childrenClassName="flex flex-col flex-1 min-h-0 overflow-hidden"
      >
        <ThreadListLayout
          id="thread-panel"
          compact
          isOpen={isListOpen}
          onOpenChange={setIsListOpen}
        >
          <Thread
            onEscape={isThreadPanelOpen ? releaseFocus : undefined}
            onExpand={onExpand}
            stickyBackdropColor={colors.panel}
          />
        </ThreadListLayout>
      </StickyLayout>
    </Scope>
  );
}
